import { useCallback, useEffect, useRef, useState } from "react";
import "@/react/components/HelpWidget/assets/styles/thinking-card.css";
import { Icon } from "@/react/components/shared/Icon";
import { Typography } from "@/react/components/shared/uikit/Typography";

interface ThinkingCardProps {
  onQueueComplete?: () => void;
  show?: boolean;
  title: string | null;
}

const MIN_DISPLAY_TIME = 2000;

export const ThinkingCard = ({
  show = true,
  title,
  onQueueComplete,
}: ThinkingCardProps) => {
  const [isVisible, setIsVisible] = useState(show);
  const [displayTitle, setDisplayTitle] = useState(title || "");
  const titleQueueRef = useRef<string[]>([]);
  const currentTitleStartTimeRef = useRef<number | null>(null);
  const processingQueueRef = useRef(false);

  const getRemainingTime = useCallback(
    () =>
      currentTitleStartTimeRef.current
        ? Math.max(
            0,
            MIN_DISPLAY_TIME - (Date.now() - currentTitleStartTimeRef.current),
          )
        : 0,
    [],
  );

  const processNextTitle = useCallback(() => {
    if (processingQueueRef.current) return;

    const queue = titleQueueRef.current;
    if (queue.length === 0) return;

    processingQueueRef.current = true;
    const nextTitle = queue[0];
    titleQueueRef.current = queue.slice(1);

    const remainingTime = getRemainingTime();

    setTimeout(() => {
      setDisplayTitle(nextTitle);
      currentTitleStartTimeRef.current = Date.now();
      processingQueueRef.current = false;

      if (titleQueueRef.current.length > 0) {
        setTimeout(() => processNextTitle(), 0);
      }
    }, remainingTime);
  }, [getRemainingTime]);

  useEffect(() => {
    if (!title) return;

    if (!displayTitle) {
      setDisplayTitle(title);
      currentTitleStartTimeRef.current = Date.now();
      return;
    }

    if (title === displayTitle) return;

    if (!titleQueueRef.current.includes(title)) {
      titleQueueRef.current = [...titleQueueRef.current, title];

      if (!processingQueueRef.current) {
        setTimeout(() => processNextTitle(), 0);
      }
    }
  }, [title, displayTitle, processNextTitle]);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      return;
    }

    if (!isVisible) return;

    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      currentTitleStartTimeRef.current = null;
      titleQueueRef.current = [];
      processingQueueRef.current = false;

      onQueueComplete?.();
    }, getRemainingTime() + 300);

    return () => {
      clearTimeout(hideTimer);
    };
  }, [show, isVisible, displayTitle, onQueueComplete, getRemainingTime]);

  if (!isVisible) return null;

  return (
    <div className="mt-4">
      <div
        id="copilot-thinking-card"
        className="bg-primary animate-slide-in flex items-start gap-1 rounded-2xl border border-[#E4E7EB] px-4 py-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-all duration-300 dark:border-[#42464D]"
      >
        <Icon
          type="20-hw-copilot-thinking-star"
          size={20}
          className="animate-spin-clock-wise"
        />

        <span className="shimmer-text">
          <Typography.LabelSm weight="medium">
            {displayTitle ?? ""}
          </Typography.LabelSm>
        </span>
      </div>
    </div>
  );
};
